import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CustomPagination from './CustomPagination';
import { Column, TableProps } from './types';

const StyledTableContainer = styled(Paper)(() => ({
  boxShadow: 'none',
  overflow: 'hidden',
}));

const StyledTableHead = styled(TableHead)(() => ({
  backgroundColor: '#F1F3FF',
  '& th': {
    fontWeight: 500,
    color: '#667085',
    borderBottom: '1px solid #EAECF0',
    padding: '6px 5px',
    fontSize: '13px !important',
  },
}));

interface StyledTableCellProps {
  align?: 'left' | 'center' | 'right';
}

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'align',
})<StyledTableCellProps>(({ align }) => ({
  padding: '5px 0 0 5px',
  color: '#000000',
  borderBottom: 'none',
  verticalAlign: 'top',
  textAlign: align || 'left',
  fontSize: '12px',
  fontWeight: 500,
}));

const StyledTableRow = styled(TableRow)({
  '&:hover': {
    backgroundColor: '#F9FAFB',
  },
});

const SortableHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  '& .MuiSvgIcon-root': {
    fontSize: 18,
    marginLeft: 4,
  },
});

const getColumnAlignment = (column: Column<any>): 'left' | 'center' | 'right' => {
  if (column.align) return column.align;

  if (column.type === 'number') return 'right';
  if (column.type === 'date' || column.type === 'status') return 'center';
  return 'left';
};

function CustomTable<T extends { [key: string]: any }>({
  columns,
  rows,
  onSort,
  sortField,
  sortDirection,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  hasPagination = true,
}: TableProps<T>) {
  const handleSort = (field: keyof T) => {
    if (onSort) {
      onSort(field);
    }
  };

  const renderSortIcon = (field: keyof T) => {
    if (field !== sortField) return null;
    return sortDirection === 'asc' ? (
      <ArrowUpwardIcon />
    ) : (
      <ArrowDownwardIcon />
    );
  };

  return (
    <StyledTableContainer>
      <TableContainer>
        <Table>
          <StyledTableHead>
            <TableRow>
              {columns.map((column) => {
                const alignment = getColumnAlignment(column);
                return (
                  <TableCell
                    key={String(column.field)}
                    style={{ width: column.width }}
                  >
                    {column.sortable ? (
                      <SortableHeader
                        onClick={() => handleSort(column.field)}
                        sx={{ justifyContent: alignment === 'center' ? 'center' : alignment === 'right' ? 'flex-end' : 'flex-start' }}
                      >
                        <Typography variant="subtitle2" sx={{ fontSize: '13px !important' }}>
                          {column.headerName}
                        </Typography>
                        {renderSortIcon(column.field)}
                      </SortableHeader>
                    ) : (
                      <Typography variant="subtitle2">
                        {column.headerName}
                      </Typography>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {rows.map((row, index) => (
              <StyledTableRow key={index}>
                {columns.map((column) => {
                  const alignment = getColumnAlignment(column);
                  return (
                    <StyledTableCell
                      key={String(column.field)}
                      align={alignment}
                    >
                      {column.renderCell
                        ? column.renderCell(row)
                        : row[column.field]}
                    </StyledTableCell>
                  );
                })}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {hasPagination && (
        <CustomPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
    </StyledTableContainer>
  );
}

export default CustomTable; 